/**
 * Centralizované konstanty kalkulaček pro rok 2026.
 * Zdroj: beplan University — Finanční kalkulačky (interní spec, duben 2026).
 *
 * OVĚŘENO 23. 8. 2026 proti veřejným zdrojům:
 *  • STAVEBKO 5 % / max 1 000 Kč / vklad 20 000 / vázací doba 6 let — SEDÍ
 *  • DPS 500 / 20 % / 340 Kč / práh 1 700, limit odpočtu 48 000, zaměstnavatel
 *    50 000 — SEDÍ (Finanční správa, MF ČR)
 *  • HYPO_DAN 150 000 (úvěry od 2021) / 300 000 (do 2020), sazba 15 % — SEDÍ
 *  • HYPO — viz podrobná poznámka u sekce; závazné je jen LTV
 *
 * Pravidla aktualizace:
 *  - DPS limity: pokud MF změní limity → upravit DPS sekci
 *  - Stavebka: ČNB / vláda mění státní podporu → STAVEBKO
 *  - LTV/DTI: ČNB rozhoduje 2× ročně (jaro/podzim) → HYPO
 *  - Daňové limity: dle zákona o dani z příjmů → HYPO_DAN
 *  - Inflace default + výnosy: revize 2× ročně
 */

export const CONFIG_2026 = {
  /* ⚠️ CO JE ZÁVAZNÝ LIMIT A CO JEN DOPORUČENÍ — nesmí se plést, protože z toho
   * poradce mluví na klienta („banka nesmí" versus „banka bývá opatrná").
   * Ověřeno 23. 8. 2026 na ČNB:
   *   • LTV 80 % / 90 % do 36 let = ZÁVAZNÉ opatření obecné povahy (s 5% objemovou
   *     výjimkou pro poskytovatele).
   *   • DTI a DSTI u úvěrů na vlastní bydlení jsou DEAKTIVOVANÉ — horní hranice
   *     není stanovena. Hodnoty 8 a 40 % jsou ČNB DOPORUČENÍ k obezřetnosti,
   *     ne zákaz. Banka smí jít výš a řada bank chodí.
   *   • Investiční nemovitosti (3. a další byt nebo na pronájem): LTV 70 a DTI 7
   *     od 1. 4. 2026 — také DOPORUČENÍ, ne závazné opatření.
   * Zdroje: https://www.cnb.cz/cs/financni-stabilita/makroobezretnostni-politika/stanoveni-horni-hranice-uverovych-ukazatelu/
   *         https://www.cnb.cz/cs/cnb-news/tiskove-zpravy/CNB-doporucuje-prisnejsi-limity-pro-investicni-hypoteky.-Kapitalove-rezervy-se-nemeni/ */
  HYPO: {
    LTV_DO_36_VLASTNI: 90,   // závazné
    LTV_NAD_36_VLASTNI: 80,  // závazné
    LTV_INVESTICNI: 70,      // DOPORUČENÍ od 1. 4. 2026
    DTI_VLASTNI: 8,          // DOPORUČENÍ (horní hranice deaktivovaná)
    DTI_INVESTICNI: 7,       // DOPORUČENÍ od 1. 4. 2026
    DSTI: 40,                // DOPORUČENÍ (horní hranice deaktivovaná)
    /* Strojově čitelné, ať to UI nemusí hádat z komentáře. */
    ZAVAZNE: ["LTV_DO_36_VLASTNI", "LTV_NAD_36_VLASTNI"],
    DOPORUCENE: ["LTV_INVESTICNI", "DTI_VLASTNI", "DTI_INVESTICNI", "DSTI"],
    INVESTICNI_OD: "2026-04-01",
    OVERENO: "2026-08-23",
  },
  STAVEBKO: {
    STATNI_PODPORA_PROCENT: 5,
    STATNI_PODPORA_MAX: 1000,
    VKLAD_PRO_MAX: 20000,
    VAZACI_DOBA: 6,
    DAN_Z_VYNOSU: 15,
  },
  DPS: {
    MIN_VKLAD_PRO_PRISPEVEK: 500,
    MAX_PRISPEVEK: 340,
    PROCENT_PRISPEVKU: 20,
    LIMIT_PRO_DAN_OD: 1700,
    PRISPEVEK_ZAMESTNAVATELE_MAX: 50000,
    DAN_LIMIT_OD_2024: 48000,
  },
  DIP: {
    DAN_LIMIT_ROCNE: 48000,
    PRISPEVEK_ZAMESTNAVATELE_MAX: 50000,
    MIN_DOBA_LET: 10,
    MIN_VEK_VYBER: 60,
  },
  HYPO_DAN: {
    LIMIT_OD_2021: 150000,
    LIMIT_DO_2020: 300000,
    SAZBA_DANE: 15,
  },
  /* Úvěr na cíl mimo bydlení (auto…) v Návrhu beta — majitel 28. 10. 2026: „auto úvěrem
     není potřeba 10 %, lze vzít spotřebák na 100 %, jen u drahých aut 2–3 mil. je
     potřeba 10, 20, 30 %“. Od této ceny výchozí vlastní zdroje 20 %, jinak 0 %. */
  UVER_MIMO_BYDLENI: {
    DRAHY_OD: 1500000,
    DRAHY_OD_EUR: 60000,
    VLASTNI_DRAHY_PCT: 20,
  },
  /* Detail cíle v Návrhu beta (majitel 28. 10. 2026, „ano na vše“):
     auto se plánuje nejvýš na 10 let (pole „za kolik let“ v detailu i v řádku);
     nemovitostní fond u krátkých cílů (do 10 let) = MODELOVÝ pevný výnos 5,5 % ročně
     (reálně, v dnešních penězích; poradce přepíše v detailu), nízké kolísání, ne garance. */
  CILE_NAVRH: {
    AUTO_MAX_LET: 10,
    FOND_VYNOS_PCT: 5.5,
    FOND_DO_LET: 10,
  },
  DEFAULTS: {
    INFLACE: 2.5,
    /* Průměrná NABÍDKOVÁ sazba hypoték — Swiss Life Hypoindex 8/2026 = 5,42 %
     * (7/2026 5,32 %, 6/2026 5,30 %; rostla pět měsíců v řadě). Bylo tu 4,9 %,
     * což byla hodnota z března 2026 — modelace tím klientovi počítala nižší
     * splátku, než jakou by reálně dostal. Je to jen VÝCHOZÍ hodnota šoupátka,
     * ne garance sazby; poradce zadá konkrétní nabídku banky.
     * Zdroj: https://www.hypoindex.cz/hypoindex-vyvoj/ — ověřeno 23. 8. 2026. */
    HYPO_SAZBA: 5.42,
    HYPO_SAZBA_OBDOBI: "8/2026",
    SPORICI_SAZBA: 3.0,
    DLUHOPISY_SAZBA: 4.5,
    SMISENE_SAZBA: 6.0,
    AKCIE_SAZBA: 7.0,
    DAN_Z_VYNOSU: 15,
    /* Spotřebitelské úvěry v Návrhu (beta), detail „Jak to vyřešit“ — MODELOVÉ
     * sazby, poradce přepíše podle nabídky banky (majitel 28. 10. 2026, otázka 6).
     * Úvěr ze stavebka nejdéle 20 let. */
    DLUH_SLOUCENI_SAZBA: 8.9,
    DLUH_REFI_SAZBA: 6.9,
    DLUH_STAVEBKO_SAZBA: 5.9,
    DLUH_STAVEBKO_MAX_LET: 20,
    /* Majitel 28. 10. večer: „stavebko vždy 20 let“ — výchozí doba úvěru ze stavebka. */
    DLUH_STAVEBKO_ROKY: 20,
    /* Cesta „Přidat do hypotéky“ bez cíle Bydlení: neúčelová („americká“) hypotéka
     * se zástavou vlastní nemovitosti — MODELOVÁ sazba a doba, banka půjčí nejvýš
     * 70 % hodnoty zástavy (praxe bank, ne limit ČNB) → čip „ověřit u banky“. */
    DLUH_ZASTAVA_SAZBA: 5.9,
    DLUH_ZASTAVA_ROKY: 20,
    DLUH_ZASTAVA_MAX_LET: 30,
    DLUH_ZASTAVA_LTV: 70,
  },
};

/** Univerzální anuitní splátka. */
export function anuita(principal, ratePctYear, years) {
  if (!principal || !ratePctYear || !years) return 0;
  const i = ratePctYear / 100 / 12;
  const n = years * 12;
  if (i === 0) return principal / n;
  return (principal * (i * Math.pow(1 + i, n))) / (Math.pow(1 + i, n) - 1);
}

/** Zůstatek jistiny po `monthsPaid` měsících standardní anuity. */
export function zustatekJistiny(principal, ratePctYear, totalYears, monthsPaid) {
  const i = ratePctYear / 100 / 12;
  const n = totalYears * 12;
  if (i === 0) return Math.max(0, principal - (principal / n) * monthsPaid);
  const factor = Math.pow(1 + i, n);
  const factorPaid = Math.pow(1 + i, monthsPaid);
  return Math.max(0, principal * ((factor - factorPaid) / (factor - 1)));
}

/** Společná validace – vrací error message nebo null. */
export function validateCalc(value, min, max, fieldName) {
  if (typeof value !== "number" || Number.isNaN(value)) return `${fieldName}: musí být číslo`;
  if (value < min) return `${fieldName}: minimum je ${min}`;
  if (value > max) return `${fieldName}: maximum je ${max}`;
  return null;
}
